/**
 * Walkthrough shown in the Settings sheet's Reminders block when
 * push is supported on the device but Almanac isn't installed as
 * a home-screen PWA — the precondition for iOS to deliver push.
 *
 * Two branches:
 *
 *  - Safari (the working path): just the Add to Home Screen steps.
 *  - Chrome / Firefox on iOS (the gotcha): an extra leading step
 *    telling the user to open the same URL in Safari first.
 *    iOS Chrome's "Add to Home Screen" looks identical to
 *    Safari's, but the resulting installed shortcut runs in a
 *    WebKit shell that doesn't fully wire up service-worker
 *    push delivery — Apple's push service accepts the payload
 *    and the device never displays it. We learned this the hard
 *    way during the prod debugging session, and the only fix is
 *    a docs hint at the install moment.
 *
 * Uses `window.location.host` rather than a hardcoded domain so
 * the same component works in preview and prod.
 */
export function IosInstallHint() {
  const nonSafari = isNonSafariIOSBrowser();
  const host = typeof window === 'undefined' ? 'almanac' : window.location.host;
  return (
    <div className="ios-install-hint">
      <p className="ios-install-hint__intro">
        {nonSafari
          ? `Reminders need Almanac installed via Safari. Other iOS browsers can “Add to Home Screen” too, but iOS won't deliver push notifications to those installs.`
          : 'Reminders need Almanac on your home screen — iOS only delivers push notifications to installed PWAs.'}
      </p>
      <ol className="ios-install-hint__steps">
        {nonSafari && (
          <li>
            Open <strong>{host}</strong> in <strong>Safari</strong>.
          </li>
        )}
        <li>
          Tap the <strong>Share</strong> button at the bottom of Safari.
        </li>
        <li>
          Scroll down and choose <strong>Add to Home Screen</strong>.
        </li>
        <li>Open Almanac from the new icon on your home screen.</li>
        <li>Come back here to turn reminders on.</li>
      </ol>
    </div>
  );
}

/** True when the current browser is iOS but not Safari. iOS
 *  Chrome, Firefox, and other browsers all run on top of WebKit
 *  but identify themselves with distinct UA tokens (`CriOS`,
 *  `FxiOS`, `EdgiOS`); any of them produces an install that
 *  iOS won't push-deliver to. */
function isNonSafariIOSBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (!/iPhone|iPad|iPod/u.test(ua)) return false;
  return /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/u.test(ua);
}
